import { Link } from "react-router-dom";
import {
  Sparkles, SlidersHorizontal, Copy, Users, Blend, Repeat, Megaphone, Music, ArrowRight,
} from "lucide-react";
import { Seo } from "../components/Seo";
import { PublicLayout, OpenGeneratorButton } from "../components/PublicLayout";
import site from "../content/site.json";

const H = site.home;
const STEP_ICONS = [SlidersHorizontal, Sparkles, Copy];
const USE_ICONS = [Users, Blend, Repeat, Megaphone];

export default function Landing() {
  return (
    <PublicLayout>
      <Seo
        title={H.title}
        description={H.description}
        path="/"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          name: "AI Song Prompt Studio",
          applicationCategory: "MultimediaApplication",
          operatingSystem: "Web",
          offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
          description: H.description,
        }}
      />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <img src="/og-image.jpg" alt="" aria-hidden="true" loading="eager"
          className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#070B14] via-[#070B14]/90 to-[#070B14]/40" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-2 rounded-full border border-[#22D3EE]/30 bg-[#22D3EE]/10 px-3 py-1 text-xs font-semibold text-[#8BE8F5]">
              <Music className="w-3.5 h-3.5" /> Suno prompt generator
            </span>
            <h1 className="mt-5 font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-50 leading-[1.05]">
              {H.h1}
            </h1>
            <p className="mt-5 text-base sm:text-lg text-slate-300 leading-relaxed">{H.intro}</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link to="/generator" data-testid="hero-create-prompt"
                className="inline-flex items-center justify-center gap-2 rounded-full font-bold bg-gradient-to-r from-[#E6C66E] to-[#D4AF37] text-[#0B0E14] px-7 py-3.5 text-base shadow-[0_0_30px_rgba(212,175,55,0.35)] hover:opacity-90 transition-opacity">
                <Sparkles className="w-5 h-5" /> Create a Song Prompt
              </Link>
              <Link to="/faq"
                className="inline-flex items-center justify-center gap-2 rounded-full font-semibold border border-[#1E2C48] text-slate-200 px-6 py-3.5 hover:border-[#22D3EE]/50 transition-colors">
                Read the FAQ <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <p className="mt-5 text-xs text-slate-500 max-w-lg">{H.disclaimer}</p>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-14">
        <h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-50">How it works</h2>
        <p className="mt-2 text-slate-400 max-w-2xl">From idea to a complete Suno style prompt and lyrics in three steps.</p>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-5">
          {H.steps.map((s, i) => {
            const Icon = STEP_ICONS[i] || Sparkles;
            return (
              <div key={s.title} className="rounded-2xl border border-[#1E2C48] bg-[#0D1424] p-6">
                <div className="w-11 h-11 rounded-xl bg-[#121B30] border border-[#1E2C48] flex items-center justify-center">
                  <Icon className="w-5 h-5 text-[#22D3EE]" />
                </div>
                <h3 className="mt-4 font-heading text-lg font-semibold text-slate-100">{s.title}</h3>
                <p className="mt-2 text-sm text-slate-400 leading-relaxed">{s.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Example output */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-14">
        <h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-50">What your result looks like</h2>
        <p className="mt-2 text-slate-400 max-w-2xl">
          Every generation returns four copyable fields. Below is a generic sample to show the format —
          your results are original and based on your own choices.
        </p>
        <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div className="rounded-2xl border border-[#1E2C48] bg-[#0D1424] overflow-hidden">
            <div className="px-4 py-2.5 border-b border-[#1E2C48] font-label text-[#22D3EE]">Song Title</div>
            <p className="p-4 font-heading text-lg font-bold text-slate-50">{H.example.title}</p>
            <div className="px-4 py-2.5 border-y border-[#1E2C48] font-label text-[#D4AF37]">Suno Styles Prompt</div>
            <p className="p-4 text-sm text-slate-200 leading-relaxed">{H.example.styles}</p>
            <div className="px-4 py-2.5 border-y border-[#1E2C48] font-label text-[#FF7A66]">Exclude</div>
            <p className="p-4 text-sm text-slate-300">{H.example.exclude}</p>
          </div>
          <div className="rounded-2xl border border-[#1E2C48] bg-[#0D1424] overflow-hidden">
            <div className="px-4 py-2.5 border-b border-[#1E2C48] font-label text-amber-400/90">Complete Lyrics</div>
            <pre className="p-4 font-body text-sm text-slate-200 whitespace-pre-wrap leading-relaxed">{H.example.lyrics}</pre>
          </div>
        </div>
        <p className="mt-3 text-xs text-slate-500">Sample shown for format only. Not a real or reserved song.</p>
      </section>

      {/* Use cases */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-14">
        <h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-50">Built for ambitious songs</h2>
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-5">
          {H.useCases.map((u, i) => {
            const Icon = USE_ICONS[i] || Users;
            return (
              <div key={u.title} className="rounded-2xl border border-[#1E2C48] bg-[#0D1424] p-6 flex gap-4">
                <div className="w-11 h-11 shrink-0 rounded-xl bg-[#121B30] border border-[#1E2C48] flex items-center justify-center">
                  <Icon className="w-5 h-5 text-[#FF7A66]" />
                </div>
                <div>
                  <h3 className="font-heading text-lg font-semibold text-slate-100">{u.title}</h3>
                  <p className="mt-1.5 text-sm text-slate-400 leading-relaxed">{u.body}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Audio demos placeholder */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-14">
        <div className="rounded-2xl border border-dashed border-[#1E2C48] bg-[#0D1424]/50 p-8 text-center">
          <Music className="w-8 h-8 text-[#22D3EE]/70 mx-auto" />
          <h2 className="mt-4 font-heading text-xl font-bold text-slate-100">Audio demos coming soon</h2>
          <p className="mt-2 text-sm text-slate-400 max-w-xl mx-auto">
            Approved audio demos will appear here so you can hear what songs built from these prompts sound
            like. In the meantime, try the generator and create your own.
          </p>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-16">
        <div className="rounded-3xl border border-[#1E2C48] bg-gradient-to-br from-[#0D1424] to-[#121B30] p-8 sm:p-12 text-center">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-50">Start your next Suno song</h2>
          <p className="mt-3 text-slate-400 max-w-xl mx-auto">
            Open the generator to build a title, Suno style prompt, and complete lyrics now.
          </p>
          <div className="mt-7 flex justify-center">
            <OpenGeneratorButton testId="landing-bottom-open-generator" label="Open Song Generator" className="h-12 px-7 text-base" />
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
